import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Search, Ban, CheckCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUsers, createUser, updateUser, setUserBlockStatus, deleteUser } from "@/integrations/supabase/user-services";
import { getBranches } from "@/integrations/supabase/services";
import { UserProfile } from "@/types/models";
import { useAuth } from "@/context/AuthContext";

// Form validation schema
const userFormSchema = z.object({
  email: z.string().email({ message: "Email inválido" }),
  password: z.string().min(6, { message: "La contraseña debe tener al menos 6 caracteres" }).optional(),
  full_name: z.string().min(2, { message: "El nombre debe tener al menos 2 caracteres" }),
  role: z.enum(["super_admin", "branch_admin", "operator"], { required_error: "El rol es requerido" }),
  branch_id: z.string({ required_error: "La sucursal es requerida" }),
});

type UserFormValues = z.infer<typeof userFormSchema>;

const Users = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [search, setSearch] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile, session } = useAuth();
  
  // Fetch users
  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers(),
    enabled: profile?.role === 'super_admin' || profile?.role === 'branch_admin'
  });

  const filteredUsers = users.filter(
    (user) =>
      user.full_name?.toLowerCase().includes(search.toLowerCase())
  );
  
  // Fetch branches for branch selection
  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: getBranches,
    enabled: profile?.role === 'super_admin' || profile?.role === 'branch_admin'
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (userData: UserFormValues) => {
      const apiData: any = { ...userData };
      if (!apiData.password) delete apiData.password;
      return createUser(apiData); 
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: "Usuario creado correctamente" });
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({ 
        variant: "destructive", 
        title: "Error al crear usuario", 
        description: error.message 
      });
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({    
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: "Usuario actualizado correctamente" });
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({ 
        variant: "destructive", 
        title: "Error al actualizar usuario", 
        description: error.message 
      });
    },
  });

  // Block/Unblock user mutation
  const setUserBlockStatusMutation = useMutation({
    mutationFn: (params: { userId: string; isBlocked: boolean }) => 
      setUserBlockStatus(params.userId, params.isBlocked),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] }); 
      toast({ 
        title: `Usuario ${variables.isBlocked ? 'bloqueado' : 'desbloqueado'} correctamente` 
      });
    },
    onError: (error: Error, variables) => {
      toast({ 
        variant: "destructive", 
        title: `Error al ${variables.isBlocked ? 'bloquear' : 'desbloquear'} usuario`, 
        description: error.message 
      });
    },
  });

  // Delete user mutation (eventualmente será soft delete)
  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: "Usuario eliminado correctamente" });
      setIsConfirmDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ 
        variant: "destructive", 
        title: "Error al eliminar usuario", 
        description: error.message 
      });
    },
  });

  // Form
  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: undefined,
      password: undefined,
      full_name: "",
      role: "operator",
      branch_id: "",
    },
  });

  // Open dialog to add new user
  const handleAddUser = () => {
    form.reset({
      email: "",
      password: "",
      full_name: "",
      role: "operator",
      branch_id: undefined,
    });
    setSelectedUser(null);
    setIsDialogOpen(true);
  };

  // Open dialog to edit user
  const handleEditUser = (user: UserProfile) => {
    form.reset({
      email: user.email || "",
      full_name: user.full_name || "",
      role: user.role || "operator",
      branch_id: user.branch_id || undefined,
    });
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  // Open confirm dialog to delete user
  const handleDeleteUser = (user: UserProfile) => {
    setSelectedUser(user);
    setIsConfirmDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    form.reset();
  };

  // Submit form
  const onSubmit = (values: UserFormValues) => {
    if (selectedUser) {
      // Update user   
      updateUserMutation.mutate({
        id: selectedUser.id,
        full_name: values.full_name,
        role: values.role,
        branch_id: values.branch_id,
        email: values.email,
      });
    } else {
      // Create user
      if (!values.email) {
        toast({ 
          variant: "destructive", 
          title: "Error de Validación", 
          description: "El email es requerido para crear un nuevo usuario" 
        });
        form.setError("email", { type: "manual", message: "El email es requerido." });
        return;
      }
  
      if (!values.password) {
        toast({ 
          variant: "destructive", 
          title: "Error de Validación", 
          description: "La contraseña es requerida para crear un nuevo usuario" 
        });
        form.setError("password", { type: "manual", message: "La contraseña es requerida." });
        return;
      }
    
      createUserMutation.mutate(values);
    }
  };

  // Confirm delete
  const confirmDelete = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  // if (authLoading) {
  //   return (
  //     <MainLayout>
  //       <div className="flex items-center justify-center h-full">
  //         <span className="loading loading-spinner text-primary"></span>
  //       </div>
  //     </MainLayout>
  //   );
  // }
  //TODO: Mejorar carga de página
  // Only super_admin and branch_admin should access this page
  // if (profile?.role !== 'super_admin' && profile?.role !== 'branch_admin') {
  //   return (
  //     <MainLayout>
  //       <div className="flex flex-col items-center justify-center h-full">
  //         <h1 className="text-2xl font-bold text-red-600">Acceso Denegado</h1>
  //         <p className="text-gray-500">No tienes permisos para ver esta página</p>
  //       </div>
  //     </MainLayout>
  //   );
  // }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Usuarios</h1>
          <Button onClick={handleAddUser} className="flex items-center gap-2">
            <Plus size={16} />
            Nuevo Usuario
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar usuarios..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead className="w-[100px] text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center h-24">
                    Cargando usuarios...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center h-24 text-red-500">
                    Error cargando los usuarios.
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No hay usuarios registrados
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} 
                    className={user.banned_at ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-100'}
                  >
                    <TableCell className="py-2">{user.full_name || "Sin nombre"}</TableCell>
                    <TableCell className="py-2">{user.email}</TableCell>
                    <TableCell className="capitalize py-2">{user.role || "operator"}</TableCell>
                    <TableCell className="py-2">{branches?.find(b => b.id === user.branch_id)?.name || "No asignada"}</TableCell>
                    <TableCell className="py-2">
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          size="icon"
                          title="Editar"
                          onClick={() => handleEditUser(user)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Eliminar (Próximamente Borrado Suave)"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <Trash2 size={16} />
                        </Button>
                        {user.banned_at ? (
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-green-500 hover:text-green-600"
                          title="Desbloquear Usuario"
                          onClick={() => {
                            if (confirm(`¿Estás seguro que deseas DESBLOQUEAR al usuario ${user.full_name}?`)) {
                              setUserBlockStatusMutation.mutate({ userId: user.id, isBlocked: false });
                            }
                          }}
                          disabled={setUserBlockStatusMutation.isPending && setUserBlockStatusMutation.variables?.userId === user.id && setUserBlockStatusMutation.variables?.isBlocked === false}
                        >
                          <CheckCircle size={16} />
                        </Button>
                        ) : (
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-orange-500 hover:text-orange-600"
                          title="Bloquear Usuario"
                          onClick={() => {
                            if (confirm(`¿Estás seguro que deseas BLOQUEAR al usuario ${user.full_name}?`)) {
                              setUserBlockStatusMutation.mutate({ userId: user.id, isBlocked: true });
                            }
                          }}
                          disabled={setUserBlockStatusMutation.isPending && setUserBlockStatusMutation.variables?.userId === user.id && setUserBlockStatusMutation.variables?.isBlocked === true}
                        >
                          <Ban size={16} />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* User Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? "Editar Usuario" : "Nuevo Usuario"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {!selectedUser && (
                <>
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contraseña</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Contraseña" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre Completo</FormLabel>
                    <FormControl>
                      <Input placeholder="Nombre Completo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rol</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar rol" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                        <SelectItem value="branch_admin">Admin Sucursal</SelectItem>
                        <SelectItem value="operator">Operador</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="branch_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sucursal</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || ""} defaultValue={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar sucursal" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {branches?.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={createUserMutation.isPending || updateUserMutation.isPending || setUserBlockStatusMutation.isPending}
                >
                  {
                    createUserMutation.isPending || updateUserMutation.isPending ? 
                      'Guardando...' : selectedUser ? 'Actualizar' : 'Crear'
                  }
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>¿Estás seguro que deseas eliminar este usuario?</p>
            <p className="font-medium">{selectedUser?.full_name}</p>
            <p className="text-sm text-gray-500 mt-2">Esta acción será un borrado suave (el usuario será archivado).</p>
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={deleteUserMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Users;
